import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { API_URL, getAuthHeader } from '@/lib/utils';
import { Plus, Pencil, Trash2, FileText, Upload, Loader2, Download } from 'lucide-react';

const categories = ['General', 'Services', 'Products', 'Courses', 'Company'];

const AdminBrochures = () => {
  const [brochures, setBrochures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBrochure, setEditingBrochure] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    file_url: '',
    category: 'general',
    is_active: true
  });

  const fetchBrochures = async () => {
    try {
      const res = await axios.get(`${API_URL}/brochures`);
      setBrochures(res.data);
    } catch (error) {
      toast.error('Failed to fetch brochures');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBrochures(); }, []);

  const resetForm = () => {
    setFormData({ title: '', description: '', file_url: '', category: 'general', is_active: true });
    setEditingBrochure(null);
  };

  const handleEdit = (brochure) => {
    setEditingBrochure(brochure);
    setFormData({
      title: brochure.title,
      description: brochure.description || '',
      file_url: brochure.file_url,
      category: brochure.category || 'general',
      is_active: brochure.is_active
    });
    setDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Max 10MB');
      return;
    }

    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(`${API_URL}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      setFormData(prev => ({ ...prev, file_url: `${API_URL}/files/${res.data.id}` }));
      toast.success('PDF uploaded successfully');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.file_url) {
      toast.error('Title and PDF file are required');
      return;
    }
    try {
      if (editingBrochure) {
        await axios.put(`${API_URL}/brochures/${editingBrochure.id}`, formData, { headers: getAuthHeader() });
        toast.success('Brochure updated');
      } else {
        await axios.post(`${API_URL}/brochures`, formData, { headers: getAuthHeader() });
        toast.success('Brochure created');
      }
      setDialogOpen(false);
      resetForm();
      fetchBrochures();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this brochure?')) return;
    try {
      await axios.delete(`${API_URL}/brochures/${id}`, { headers: getAuthHeader() });
      toast.success('Deleted');
      fetchBrochures();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div data-testid="admin-brochures">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-manrope text-2xl font-bold text-[#1a2a5e]">Brochures</h1>
          <p className="text-gray-500 mt-1">Upload and manage PDF brochures for download</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1a2a5e] hover:bg-[#2d4a8e]">
              <Plus className="mr-2 h-4 w-4" />
              Add Brochure
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingBrochure ? 'Edit' : 'Add'} Brochure</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Brochure title"
                  data-testid="brochure-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c.toLowerCase()} value={c.toLowerCase()}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>PDF File *</Label>
                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-[#1a2a5e] hover:bg-slate-50 transition-colors"
                  onClick={() => fileRef.current?.click()}
                  data-testid="brochure-upload-dropzone"
                >
                  {uploading ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="h-8 w-8 text-[#1a2a5e] animate-spin mb-2" />
                      <p className="text-sm text-gray-500">Uploading PDF...</p>
                    </div>
                  ) : formData.file_url ? (
                    <div className="flex flex-col items-center">
                      <FileText className="h-8 w-8 text-[#2eb24c] mb-2" />
                      <p className="text-sm font-medium text-green-700">PDF uploaded</p>
                      <p className="text-xs text-gray-400 mt-1">Click to replace</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                      <p className="text-sm font-medium text-gray-700">Click to upload PDF</p>
                      <p className="text-xs text-gray-400 mt-1">Max 10MB</p>
                    </div>
                  )}
                </div>
                <input type="file" ref={fileRef} onChange={handleFileUpload} accept=".pdf" className="hidden" />
                <Input
                  value={formData.file_url}
                  onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                  placeholder="Or paste file URL"
                  className="mt-2"
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label>Active</Label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#1a2a5e]">
                  {editingBrochure ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Downloads</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {brochures.map((b) => (
                <TableRow key={b.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-red-500" />
                      <span className="font-medium">{b.title}</span>
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="capitalize">{b.category}</Badge></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      <Download className="h-3.5 w-3.5" /> {b.downloads || 0}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={b.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                      {b.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" asChild>
                      <a href={b.file_url} target="_blank" rel="noopener noreferrer"><FileText className="h-4 w-4 text-blue-500" /></a>
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(b)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(b.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {brochures.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    No brochures yet. Upload your first PDF brochure!
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminBrochures;
